import React, { useRef } from "react";
import { OktaAuth } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";

import { IAuthStore } from "stores/AuthStore";

import { SessionChecker } from "./SessionChecker";
import { AuthContext, AuthOptions } from "./AuthContext";

export interface SecurityConfiguration
{
  openIdConfiguration: OpenIdConfiguration;
  onAuthRequired?: () => void;
  onAuthenticated?: () => void;
}
export interface OpenIdConfiguration
{
  authority: string;
  clientId: string;
  loginCallbackUrl: string;
}

interface Props
{
  children: React.ReactNode;
  authStore: IAuthStore;
  config: SecurityConfiguration;
  accessDenied?: React.ReactNode;
  accessDeniedUrl?: string;
  redirectToAccessDeniedUrl?: () => void;
}
export const AuthSecurity: React.FC<Props> = ({authStore, config, accessDenied, accessDeniedUrl, redirectToAccessDeniedUrl, children}) =>
{
  const isInProgress = useRef(false);
  const oktaAuth = new OktaAuth(
    {
      issuer: config.openIdConfiguration.authority,
      clientId: config.openIdConfiguration.clientId,
      redirectUri: config.openIdConfiguration.loginCallbackUrl,
    });
  const onAuthRequired = () => config.onAuthRequired ? config.onAuthRequired() : null;
  const onAuthenticated = () => config.onAuthenticated ? config.onAuthenticated() : null;
  const restoreOriginalUri = (_oktaAuth: OktaAuth, _originalUri?: string) => { onAuthenticated(); };
  const options: AuthOptions =
    {
      authStore: authStore,
      signInAsync: async () =>
      {
        if (await oktaAuth.isAuthenticated())
        {
          return;
        }
        await oktaAuth.signInWithRedirect();
      },
      signOutAsync: async () =>
      {
        await oktaAuth.signOut();
        authStore.setJwt(null);
      },
      isInProgress: () => isInProgress.current,
      accessDenied: accessDenied ?? <></>,
      accessDeniedUrl: accessDeniedUrl,
      redirectToAccessDeniedUrl: redirectToAccessDeniedUrl,
    };
  oktaAuth.authStateManager.subscribe(async (state) =>
    {
      if (state.isAuthenticated && state.accessToken)
      {
        if (!authStore.currentUser && !isInProgress.current)
        {
          isInProgress.current = true;
          authStore.setJwt(state.accessToken.accessToken);
          isInProgress.current = false;
        }
      }
      else
      {
        // it is fine till internal token is valid
      }
    });

  return (
    <AuthContext.Provider value={options}>
      <Security oktaAuth={oktaAuth}
                restoreOriginalUri={restoreOriginalUri} >
         <SessionChecker onAuthRequired={onAuthRequired}>{children}</SessionChecker>
      </Security>
    </AuthContext.Provider>
  );
};